import { supabase } from './supabase';
import { calculatePrice } from '../utils/pricingEngine';
import { formatTime12h, formatDateMMDDYYYY } from './utils';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_CONFIRMATION_PAGE = {
  title: 'Request Received!',
  message: "<p>We've received your booking request. An admin will review it shortly. You'll receive an email update soon.</p>",
  buttons: [{ label: 'Back to Home', url: '/', style: 'primary' }]
};

// ============ MAPPERS ============

function mapEventTypeFromDb(row) {
  const cp = row.confirmation_page;
  const hasConfirmation = cp && (cp.title || cp.message || cp.buttons);
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    baseRate: Number(row.base_rate),
    minDuration: row.min_duration,
    cooldownHours: row.cooldown_hours,
    active: row.active,
    color: row.color,
    imageUrl: row.image_url || null,
    fullDayCapWeekday: row.full_day_cap_weekday != null ? Number(row.full_day_cap_weekday) : null,
    fullDayCapWeekend: row.full_day_cap_weekend != null ? Number(row.full_day_cap_weekend) : null,
    isFlatPackage: row.is_flat_package || false,
    flatPackagePrice: row.flat_package_price != null ? Number(row.flat_package_price) : null,
    requiresAdminApproval: row.requires_admin_approval !== false,
    allowOfflinePayment: row.allow_offline_payment || false,
    bundledAddons: row.bundled_addons || [],
    emailTemplates: row.email_templates || {},
    confirmationPage: hasConfirmation
      ? { ...DEFAULT_CONFIRMATION_PAGE, ...cp, buttons: cp.buttons || DEFAULT_CONFIRMATION_PAGE.buttons }
      : { ...DEFAULT_CONFIRMATION_PAGE },
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapEventTypeToDb(data) {
  return {
    name: data.name,
    description: data.description,
    base_rate: Number(data.baseRate),
    min_duration: Number(data.minDuration),
    cooldown_hours: Number(data.cooldownHours),
    active: data.active,
    color: data.color,
    image_url: data.imageUrl || null,
    full_day_cap_weekday: data.fullDayCapWeekday != null ? Number(data.fullDayCapWeekday) : null,
    full_day_cap_weekend: data.fullDayCapWeekend != null ? Number(data.fullDayCapWeekend) : null,
    is_flat_package: data.isFlatPackage || false,
    flat_package_price: data.flatPackagePrice != null ? Number(data.flatPackagePrice) : null,
    requires_admin_approval: data.requiresAdminApproval !== false,
    allow_offline_payment: data.allowOfflinePayment || false,
    bundled_addons: data.bundledAddons || [],
    email_templates: data.emailTemplates || {},
    confirmation_page: data.confirmationPage || {},
    updated_at: new Date().toISOString(),
  };
}

function mapPricingRuleFromDb(row) {
  return {
    id: row.id,
    name: row.name,
    eventTypeId: row.event_type_id,
    days: row.days || [],
    startTime: row.start_time?.slice(0, 5),
    endTime: row.end_time?.slice(0, 5),
    hourlyRate: Number(row.hourly_rate),
  };
}

function mapPricingRuleToDb(data) {
  return {
    name: data.name,
    event_type_id: data.eventTypeId || null,
    days: data.days,
    start_time: data.startTime,
    end_time: data.endTime,
    hourly_rate: Number(data.hourlyRate),
    updated_at: new Date().toISOString(),
  };
}

function mapAddOnCategoryFromDb(row) {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    selectionMode: row.selection_mode,
    required: row.required,
    sortOrder: row.sort_order,
    eventTypeIds: row.event_type_ids || [],
    active: row.active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapAddOnCategoryToDb(data) {
  return {
    name: data.name,
    description: data.description || '',
    selection_mode: data.selectionMode || 'single',
    required: data.required || false,
    sort_order: data.sortOrder || 0,
    event_type_ids: data.eventTypeIds || [],
    active: data.active !== false,
    updated_at: new Date().toISOString(),
  };
}

function mapAddOnFromDb(row) {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    price: Number(row.price),
    type: row.type,
    active: row.active,
    eventTypeIds: row.event_type_ids || [],
    categoryId: row.category_id || null,
    imageUrl: row.image_url || null,
    sortOrder: row.sort_order || 0,
  };
}

function mapAddOnToDb(data) {
  return {
    name: data.name,
    description: data.description,
    price: Number(data.price),
    type: data.type,
    active: data.active,
    event_type_ids: data.eventTypeIds || [],
    category_id: data.categoryId || null,
    image_url: data.imageUrl || null,
    sort_order: data.sortOrder || 0,
    updated_at: new Date().toISOString(),
  };
}

function mapBlockedDateFromDb(row) {
  return {
    id: row.id,
    date: row.date,
    reason: row.reason,
    isFullDay: row.is_full_day,
    startTime: row.start_time?.slice(0, 5) || null,
    endTime: row.end_time?.slice(0, 5) || null,
  };
}

function mapBookingFromDb(row, eventTypes) {
  const eventType = eventTypes?.find(et => et.id === row.event_type_id) || null;
  return {
    id: row.id,
    eventType,
    eventTypeId: row.event_type_id,
    date: row.date,
    startTime: row.start_time?.slice(0, 5),
    endTime: row.end_time?.slice(0, 5),
    startDate: row.start_date,
    endDate: row.end_date,
    contactName: row.contact_name,
    contactEmail: row.contact_email,
    contactPhone: row.contact_phone,
    guestCount: row.guest_count,
    descriptionOfUse: row.description_of_use,
    notes: row.notes,
    selectedAddOns: row.selected_add_on_ids || [],
    totalPrice: Number(row.total_price),
    subtotal: Number(row.subtotal || 0),
    depositAmount: Number(row.deposit_amount || 0),
    amountPaid: Number(row.amount_paid || 0),
    balanceDue: Math.round((Number(row.total_price || 0) - Number(row.amount_paid || 0)) * 100) / 100,
    paymentStatus: row.payment_status || 'unpaid',
    stripePaymentIntentId: row.stripe_payment_intent_id || null,
    stripeCustomerId: row.stripe_customer_id || null,
    accessCode: row.access_code || null,
    status: row.status,
    agreedToPolicies: row.agreed_to_policies,
    policiesViewedAt: row.policies_viewed_at,
    policyAcceptedAt: row.policy_accepted_at || null,
    acceptedPolicySnapshot: row.accepted_policy_snapshot || null,
    isOfflinePayment: row.is_offline_payment || false,
    depositPaymentIntent: row.deposit_payment_intent || null,
    balancePaymentIntent: row.balance_payment_intent || null,
    remindersSent: row.reminders_sent || {},
    balancePaymentLink: row.balance_payment_link || null,
    depositPaymentLink: row.deposit_payment_link || null,
    emailStatus: row.email_status,
    followupEmailSent: row.followup_email_sent,
    version: row.version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapSettingsFromDb(row) {
  return {
    primaryColor: row.primary_color,
    accentColor: row.accent_color,
    companyName: row.company_name,
    logo: row.logo,
    rentalPolicies: row.rental_policies || {},
    operatingHoursStart: row.operating_hours_start?.slice(0, 5) || '08:00',
    operatingHoursEnd: row.operating_hours_end?.slice(0, 5) || '22:00',
    accessCodeHoursBefore: row.access_code_hours_before || 48,
    depositPercentage: Number(row.deposit_percentage || 50),
    balanceDueHoursBefore: row.balance_due_hours_before || 48,
    invoiceScheduleDays: row.invoice_schedule_days || [14, 5],
    apiKeys: {
      resendApiKey: row.api_keys_encrypted
        ? maskApiKey(row.api_keys_encrypted)
        : '',
    },
    _rawApiKeysEncrypted: row.api_keys_encrypted,
  };
}

function maskApiKey(key) {
  if (!key || key.length < 8) return '****';
  return key.slice(0, 3) + '****' + key.slice(-4);
}

export function calculatePriceServer(eventType, startDateStr, endDateStr, rules, addOns = [], selectedAddOnIds = []) {
  return calculatePrice(eventType, startDateStr, endDateStr, rules, addOns, selectedAddOnIds);
}

// ============ EVENT TYPES ============

export async function fetchEventTypes() {
  const { data, error } = await supabase
    .from('event_types')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapEventTypeFromDb);
}

export async function createEventType(eventTypeData) {
  const dbData = mapEventTypeToDb(eventTypeData);
  const { data, error } = await supabase
    .from('event_types')
    .insert(dbData)
    .select()
    .single();
  if (error) throw error;
  return mapEventTypeFromDb(data);
}

export async function updateEventType(id, eventTypeData) {
  const dbData = mapEventTypeToDb(eventTypeData);
  const { data, error } = await supabase
    .from('event_types')
    .update(dbData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return mapEventTypeFromDb(data);
}

export async function deleteEventType(id) {
  const { error } = await supabase
    .from('event_types')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ============ PRICING RULES ============

export async function fetchPricingRules() {
  const { data, error } = await supabase
    .from('pricing_rules')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapPricingRuleFromDb);
}

export async function createPricingRule(ruleData) {
  const dbData = mapPricingRuleToDb(ruleData);
  const { data, error } = await supabase
    .from('pricing_rules')
    .insert(dbData)
    .select()
    .single();
  if (error) throw error;
  return mapPricingRuleFromDb(data);
}

export async function updatePricingRule(id, ruleData) {
  const dbData = mapPricingRuleToDb(ruleData);
  const { data, error } = await supabase
    .from('pricing_rules')
    .update(dbData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return mapPricingRuleFromDb(data);
}

export async function deletePricingRule(id) {
  const { error } = await supabase
    .from('pricing_rules')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ============ ADD-ON CATEGORIES ============

export async function fetchAddOnCategories() {
  const { data, error } = await supabase
    .from('add_on_categories')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapAddOnCategoryFromDb);
}

export async function createAddOnCategory(catData) {
  const dbData = mapAddOnCategoryToDb(catData);
  const { data, error } = await supabase
    .from('add_on_categories')
    .insert(dbData)
    .select()
    .single();
  if (error) throw error;
  return mapAddOnCategoryFromDb(data);
}

export async function updateAddOnCategory(id, catData) {
  const dbData = mapAddOnCategoryToDb(catData);
  const { data, error } = await supabase
    .from('add_on_categories')
    .update(dbData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return mapAddOnCategoryFromDb(data);
}

export async function deleteAddOnCategory(id) {
  const { error } = await supabase
    .from('add_on_categories')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ============ ADD-ONS ============

export async function fetchAddOns() {
  const { data, error } = await supabase
    .from('add_ons')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapAddOnFromDb);
}

export async function createAddOn(addOnData) {
  const dbData = mapAddOnToDb(addOnData);
  const { data, error } = await supabase
    .from('add_ons')
    .insert(dbData)
    .select()
    .single();
  if (error) throw error;
  return mapAddOnFromDb(data);
}

export async function updateAddOn(id, addOnData) {
  const dbData = mapAddOnToDb(addOnData);
  const { data, error } = await supabase
    .from('add_ons')
    .update(dbData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return mapAddOnFromDb(data);
}

export async function deleteAddOn(id) {
  const { error } = await supabase
    .from('add_ons')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ============ BLOCKED DATES ============

export async function fetchBlockedDates() {
  const { data, error } = await supabase
    .from('blocked_dates')
    .select('*')
    .order('date', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapBlockedDateFromDb);
}

export async function createBlockedDate(blockData) {
  const { data, error } = await supabase
    .from('blocked_dates')
    .insert({
      date: blockData.date,
      reason: blockData.reason || 'Manually Blocked',
      is_full_day: blockData.isFullDay !== false,
      start_time: blockData.startTime || null,
      end_time: blockData.endTime || null,
    })
    .select()
    .single();
  if (error) throw error;
  return mapBlockedDateFromDb(data);
}

export async function deleteBlockedDate(id) {
  const { error } = await supabase
    .from('blocked_dates')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ============ SETTINGS ============

export async function fetchSettings() {
  const { data, error } = await supabase
    .from('settings')
    .select('*')
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return mapSettingsFromDb(data);
}

export async function updateSettings(settingsData) {
  const dbData = {
    primary_color: settingsData.primaryColor,
    accent_color: settingsData.accentColor,
    company_name: settingsData.companyName,
    logo: settingsData.logo || null,
    rental_policies: settingsData.rentalPolicies || {},
    operating_hours_start: settingsData.operatingHoursStart || '08:00',
    operating_hours_end: settingsData.operatingHoursEnd || '22:00',
    access_code_hours_before: settingsData.accessCodeHoursBefore || 48,
    deposit_percentage: settingsData.depositPercentage || 50,
    balance_due_hours_before: settingsData.balanceDueHoursBefore || 48,
    invoice_schedule_days: settingsData.invoiceScheduleDays || [14, 5],
    updated_at: new Date().toISOString(),
  };

  if (settingsData.apiKeys?.resendApiKey &&
      !settingsData.apiKeys.resendApiKey.includes('****')) {
    dbData.api_keys_encrypted = settingsData.apiKeys.resendApiKey;
  }

  const { data, error } = await supabase
    .from('settings')
    .upsert({ id: 1, ...dbData })
    .select()
    .single();
  if (error) throw error;

  await logAudit('settings', '1', 'updated', null);

  return mapSettingsFromDb(data);
}

// ============ BOOKINGS ============

export async function fetchBookings(eventTypes) {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => mapBookingFromDb(row, eventTypes));
}

export async function fetchBookingsForCalendar() {
  const { data, error } = await supabase
    .from('bookings')
    .select('id, date, start_time, end_time, event_type_id, status')
    .in('status', ['pending', 'confirmed']);
  if (error) throw error;
  return (data || []).map(row => ({
    id: row.id,
    date: row.date,
    startTime: row.start_time?.slice(0, 5),
    endTime: row.end_time?.slice(0, 5),
    eventTypeId: row.event_type_id,
    status: row.status,
  }));
}

export async function createBooking(bookingData, pricingRules, addOns, eventTypes) {
  if (!bookingData.contactName?.trim()) throw new Error('Full name is required.');
  if (!bookingData.contactEmail?.trim()) throw new Error('Email is required.');
  if (!EMAIL_REGEX.test(bookingData.contactEmail)) throw new Error('Invalid email format.');
  if (!bookingData.contactPhone?.trim()) throw new Error('Phone number is required.');
  if (!bookingData.guestCount || bookingData.guestCount < 1) throw new Error('Guest count must be at least 1.');
  if (!bookingData.descriptionOfUse?.trim()) throw new Error('Description of use is required.');
  if (!bookingData.eventType?.id) throw new Error('Event type is required.');

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const bookingDate = new Date(bookingData.date + 'T00:00:00');
  if (bookingDate < today) throw new Error('Cannot book dates in the past.');

  if (bookingData.startTime >= bookingData.endTime) throw new Error('End time must be after start time.');

  const startDate = new Date(`${bookingData.date}T${bookingData.startTime}`);
  const endDate = new Date(`${bookingData.date}T${bookingData.endTime}`);
  const durationHours = (endDate - startDate) / (1000 * 60 * 60);

  if (!bookingData.eventType.isFlatPackage && durationHours < bookingData.eventType.minDuration) {
    throw new Error(`Minimum duration is ${bookingData.eventType.minDuration} hours for this event type.`);
  }

  // Merge bundled add-ons with user-selected ones (deduplicated)
  const bundledIds = bookingData.eventType.bundledAddons || [];
  const allAddOnIds = [...new Set([...bundledIds, ...(bookingData.selectedAddOns || [])])];

  const pricing = calculatePrice(
    bookingData.eventType,
    startDate.toISOString(),
    endDate.toISOString(),
    pricingRules,
    addOns,
    allAddOnIds
  );

  const depositAmount = Math.round(pricing.subtotal * 0.5 * 100) / 100;
  const balanceDue = Math.round(pricing.totalPrice * 100) / 100;

  const offlineDepositPaid = bookingData.offlineDepositPaid === true;
  const offlineDepositAmount = offlineDepositPaid
    ? Math.max(0, Math.round((Number(bookingData.offlineDepositAmount) || 0) * 100) / 100)
    : 0;

  const { data, error } = await supabase.rpc('create_booking_safe', {
    p_event_type_id: bookingData.eventType.id,
    p_date: bookingData.date,
    p_start_time: bookingData.startTime,
    p_end_time: bookingData.endTime,
    p_start_date: startDate.toISOString(),
    p_end_date: endDate.toISOString(),
    p_contact_name: bookingData.contactName.trim(),
    p_contact_email: bookingData.contactEmail.trim().toLowerCase(),
    p_contact_phone: bookingData.contactPhone.trim(),
    p_guest_count: bookingData.guestCount,
    p_description_of_use: bookingData.descriptionOfUse.trim(),
    p_notes: bookingData.notes?.trim() || null,
    p_selected_add_on_ids: allAddOnIds,
    p_total_price: pricing.totalPrice,
    p_agreed_to_policies: true,
    p_policies_viewed_at: new Date().toISOString(),
    p_cooldown_hours: bookingData.eventType.cooldownHours || 1,
    p_subtotal: pricing.subtotal,
    p_deposit_amount: depositAmount,
    p_balance_due: balanceDue,
    p_accepted_policy_snapshot: bookingData.acceptedPolicySnapshot || null,
    p_is_offline_payment: bookingData.isOfflinePayment || false,
    p_offline_deposit_paid: offlineDepositPaid,
    p_offline_deposit_amount: offlineDepositAmount,
  });

  if (error) {
    if (error.message?.includes('TIME_CONFLICT')) {
      throw new Error('This time slot conflicts with an existing booking. Please choose a different time.');
    }
    if (error.message?.includes('BLOCKED_DATE')) {
      throw new Error('This date/time is blocked and unavailable for booking.');
    }
    if (error.message?.includes('OPERATING_HOURS')) {
      throw new Error('Selected times are outside operating hours (8 AM - 10 PM).');
    }
    if (error.message?.includes('bookings_no_duplicate')) {
      throw new Error('A booking with these details already exists.');
    }
    throw error;
  }

  const bookingId = data;

  if (offlineDepositPaid) {
    await triggerBookingConfirmedWebhook(bookingId);
  } else {
    await triggerBookingWebhook(bookingId);
  }

  const { data: newBooking, error: bookingFetchError } = await supabase
    .from('bookings')
    .select('*')
    .eq('id', bookingId)
    .single();
  if (bookingFetchError) throw bookingFetchError;

  return mapBookingFromDb(newBooking, eventTypes);
}

export async function updateBookingStatus(id, status, userId) {
  const { data: existing, error: fetchError } = await supabase
    .from('bookings')
    .select('*')
    .eq('id', id)
    .single();
  if (fetchError) throw fetchError;

  const previousStatus = existing.status;

  const { data, error } = await supabase
    .from('bookings')
    .update({
      status,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;

  await logAudit('booking', id, 'status_changed', userId, { status: previousStatus }, { status });

  if (status === 'confirmed' && previousStatus !== 'confirmed') {
    triggerConfirmationEmail(id, userId).catch(() => {});
  }

  return data;
}

export async function updateBookingDetails(id, bookingData, version, userId, pricingRules, addOns, eventTypes, manualPriceOverride) {
  const { data: existing, error: fetchError } = await supabase
    .from('bookings')
    .select('*')
    .eq('id', id)
    .single();
  if (fetchError) throw fetchError;

  if (existing.version !== version) {
    throw new Error('VERSION_MISMATCH: This booking was modified by another user. Please refresh and try again.');
  }

  const eventType = eventTypes?.find(et => et.id === (bookingData.eventTypeId || bookingData.eventType?.id || existing.event_type_id));

  let totalPrice;
  let subtotal;
  const dateChanged = bookingData.date !== existing.date;
  const timeChanged = bookingData.startTime !== existing.start_time?.slice(0, 5) || bookingData.endTime !== existing.end_time?.slice(0, 5);

  if (manualPriceOverride != null && !isNaN(manualPriceOverride)) {
    totalPrice = Number(manualPriceOverride);
    subtotal = totalPrice;
  } else {
    totalPrice = existing.total_price;
    subtotal = existing.subtotal;
    const typeChanged = (bookingData.eventTypeId || bookingData.eventType?.id) !== existing.event_type_id;

    if ((dateChanged || timeChanged || typeChanged) && eventType && pricingRules) {
      const startDate = new Date(`${bookingData.date}T${bookingData.startTime}`);
      const endDate = new Date(`${bookingData.date}T${bookingData.endTime}`);

      const addOnIds = bookingData.selectedAddOns || existing.selected_add_on_ids || [];
      const pricing = calculatePrice(eventType, startDate.toISOString(), endDate.toISOString(), pricingRules, addOns || [], addOnIds);

      totalPrice = pricing.totalPrice;
      subtotal = pricing.subtotal;
    }
  }

  const depositAmount = Math.round(subtotal * 0.5 * 100) / 100;
  const balanceDue = Math.max(
    0,
    Math.round((totalPrice - Number(existing.amount_paid || 0)) * 100) / 100
  );

  const updateData = {
    contact_name: bookingData.contactName,
    contact_email: bookingData.contactEmail,
    contact_phone: bookingData.contactPhone,
    guest_count: bookingData.guestCount,
    description_of_use: bookingData.descriptionOfUse,
    notes: bookingData.notes || null,
    date: bookingData.date,
    start_time: bookingData.startTime,
    end_time: bookingData.endTime,
    event_type_id: bookingData.eventTypeId || bookingData.eventType?.id || existing.event_type_id,
    total_price: totalPrice,
    subtotal: subtotal,
    deposit_amount: depositAmount,
    balance_due: balanceDue,
    version: existing.version + 1,
    updated_at: new Date().toISOString(),
  };

  if (dateChanged || timeChanged) {
    updateData.start_date = new Date(`${bookingData.date}T${bookingData.startTime}`).toISOString();
    updateData.end_date = new Date(`${bookingData.date}T${bookingData.endTime}`).toISOString();
  }

  const { data, error } = await supabase
    .from('bookings')
    .update(updateData)
    .eq('id', id)
    .eq('version', version)
    .select()
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('VERSION_MISMATCH: This booking was modified by another user. Please refresh and try again.');
    }
    throw error;
  }

  await logAudit('booking', id, 'updated', userId,
    { contact_name: existing.contact_name, date: existing.date, status: existing.status },
    { contact_name: updateData.contact_name, date: updateData.date }
  );

  return data;
}

// ============ CANCEL BOOKING ============

export async function cancelBooking(id, userId) {
  const { data: existing, error: fetchError } = await supabase
    .from('bookings')
    .select('status')
    .eq('id', id)
    .single();
  if (fetchError) throw fetchError;

  if (existing.status === 'cancelled') {
    throw new Error('Booking is already cancelled.');
  }

  const { error } = await supabase
    .from('bookings')
    .update({
      status: 'cancelled',
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);
  if (error) throw error;

  await logAudit('booking', id, 'cancelled', userId, { status: existing.status }, { status: 'cancelled' });
}

// ============ MANUAL BALANCE CLEAR ============

export async function manuallyClearBalance(bookingId, userId) {
  const { data: existing, error: fetchError } = await supabase
    .from('bookings')
    .select('*')
    .eq('id', bookingId)
    .single();
  if (fetchError) throw fetchError;

  if (existing.status !== 'confirmed') {
    throw new Error('Balance can only be cleared on confirmed bookings.');
  }
  if (Number(existing.balance_due) <= 0) {
    throw new Error('This booking already has no outstanding balance.');
  }

  const newAmountPaid = Number(existing.total_price);
  const { data, error } = await supabase
    .from('bookings')
    .update({
      balance_due: 0,
      amount_paid: newAmountPaid,
      payment_status: 'paid_in_full',
      updated_at: new Date().toISOString(),
    })
    .eq('id', bookingId)
    .select()
    .single();
  if (error) throw error;

  await logAudit('booking', bookingId, 'balance_manually_cleared', userId,
    { balance_due: existing.balance_due, amount_paid: existing.amount_paid, payment_status: existing.payment_status },
    { balance_due: 0, amount_paid: newAmountPaid, payment_status: 'paid_in_full' }
  );

  return data;
}

// ============ FINAL INVOICE ============

export async function sendManualInvoice(bookingId, invoiceType = 'balance_payment', options = {}) {
  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-stripe-invoice`;
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ bookingId, invoiceType, autoApproved: options.autoApproved === true }),
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(result.error || `Invoice request failed (${response.status})`);
  }

  return result;
}

// ============ EMAIL ============

async function triggerBookingWebhook(bookingId) {
  return fireBookingWebhook(bookingId, 'booking_created');
}

export async function triggerBookingConfirmedWebhook(bookingId) {
  return fireBookingWebhook(bookingId, 'booking_confirmed');
}

async function fireBookingWebhook(bookingId, webhookEvent) {
  try {
    const webhookUrl = import.meta.env.VITE_WEBHOOK_URL;
    if (!webhookUrl) {
      console.warn(`VITE_WEBHOOK_URL not configured, skipping ${webhookEvent} webhook.`);
      return;
    }

    const { data: record, error: recordError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .maybeSingle();
    if (recordError || !record) {
      console.error(`${webhookEvent} webhook: booking not found`, recordError?.message);
      return;
    }

    const { data: eventType } = await supabase
      .from('event_types')
      .select('*')
      .eq('id', record.event_type_id)
      .maybeSingle();

    const addOnIds = record.selected_add_on_ids || [];
    let selectedAddOns = [];
    if (addOnIds.length > 0) {
      const { data: addOnRows } = await supabase
        .from('add_ons')
        .select('*')
        .in('id', addOnIds);
      selectedAddOns = addOnRows || [];
    }

    const payload = {
      webhookEvent,
      booking: buildBookingWebhookBody(record, eventType, selectedAddOns),
    };

    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const responsePayload = await response.json().catch(() => null);
    console.log(`${webhookEvent} webhook fired`, response.status, responsePayload);
    if (!response.ok) {
      console.error(`${webhookEvent} webhook failed:`, responsePayload);
    }
  } catch (err) {
    console.error(`${webhookEvent} webhook invocation error:`, err.message);
  }
}

function buildBookingWebhookBody(record, eventType, selectedAddOns) {
  return {
    ...record,
    requires_admin_approval: eventType?.requires_admin_approval !== false,
    contactName: record.contact_name,
    contactEmail: record.contact_email,
    contactPhone: record.contact_phone,
    eventDate: record.date,
    startTime: record.start_time,
    endTime: record.end_time,
    formatted_date: formatDateMMDDYYYY(record.date),
    formatted_start_time: formatTime12h(record.start_time),
    formatted_end_time: formatTime12h(record.end_time),
    eventType: eventType?.name || '',
    eventTypeDetails: eventType || null,
    guestCount: record.guest_count,
    selectedAddOns,
    totalPrice: record.total_price,
    depositAmount: record.deposit_amount || 0,
    amountPaid: record.amount_paid || 0,
    balanceDue: record.balance_due || 0,
    paymentStatus: record.payment_status || 'unpaid',
    descriptionOfUse: record.description_of_use || '',
    isOfflinePayment: record.is_offline_payment || false,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
  };
}

async function triggerConfirmationEmail(bookingId, userId) {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-confirmation-email`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ bookingId }),
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      await supabase.from('bookings').update({ email_status: 'failed' }).eq('id', bookingId);
      await supabase.from('email_logs').insert({
        booking_id: bookingId,
        type: 'confirmation',
        status: 'failed',
        error_message: result.error || `Edge function returned ${response.status}`,
      });
    }

    await logAudit('email', bookingId, 'confirmation_attempted', userId);
  } catch (err) {
    await supabase.from('bookings').update({ email_status: 'failed' }).eq('id', bookingId);
    await supabase.from('email_logs').insert({
      booking_id: bookingId,
      type: 'confirmation',
      status: 'failed',
      error_message: `Client error: ${err.message}`,
    });
  }
}

// ============ AUDIT LOG ============

async function logAudit(entityType, entityId, action, performedBy, previousValue, newValue) {
  try {
    await supabase.from('audit_logs').insert({
      entity_type: entityType,
      entity_id: entityId,
      action,
      performed_by: performedBy || null,
      previous_value: previousValue || null,
      new_value: newValue || null,
    });
  } catch {
    // audit logging should never block operations
  }
}

export async function fetchAuditLogs(bookingId) {
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*')
    .eq('entity_type', 'booking')
    .eq('entity_id', bookingId)
    .order('timestamp', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ============ DIAGNOSTICS ============

export async function triggerDunningJob() {
  const { data, error } = await supabase.functions.invoke('process-balance-reminders', {
    method: 'POST',
  });
  if (error) throw new Error(error.message || 'Failed to invoke dunning job');
  return data;
}

export async function triggerDailyRecap() {
  const { data, error } = await supabase.functions.invoke('send-daily-recap', {
    method: 'POST',
  });
  if (error) throw new Error(error.message || 'Failed to send daily recap');
  return data;
}

export { logAudit };
